import { IconArrowRight } from '@tabler/icons-react'
import { Badge, Group, Image, Text } from 'components'
import { createStyles, getStylesRef } from '@mantine/core'
import { Link } from 'react-router-dom'
import {
  BADGE_ICON_SIZE,
  ROW_ARROW_ICON_SIZE,
  ROW_TITLE_LINE_HEIGHT,
  THUMBNAIL_FETCH_SCALE,
  THUMBNAIL_HEIGHT,
  THUMBNAIL_WIDTH,
} from '../constants'
import type { HubTag, SanityArticleListItem } from '../interfaces'
import { buildSanityImageUrl } from '../services/sanityImage'

interface ArticleRowStylesParams {
  color: string
}

const useStyles = createStyles((theme, { color }: ArticleRowStylesParams) => {
  const arrow = getStylesRef('arrow')
  const palette = theme.colors[color] ?? theme.colors[theme.primaryColor]
  const isDark = theme.colorScheme === 'dark'

  return {
    article: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      borderRadius: theme.radius.md,
      textDecoration: 'none',
      transition: 'background-color 120ms ease, color 120ms ease',
      // :link/:visited included so the legacy global anchor styles
      // (bootstrap/all.scss) never win the specificity race.
      '&, &:link, &:visited': {
        color: isDark ? theme.colors.gray[3] : theme.colors.gray[8],
      },
      '&:hover, &:visited:hover': {
        backgroundColor: isDark ? theme.fn.rgba(palette[8], 0.25) : palette[0],
        color: isDark ? palette[2] : palette[7],
      },
      [`&:hover .${arrow}`]: {
        opacity: 1,
        transform: 'translateX(0)',
      },
    },
    thumbnail: {
      flexShrink: 0,
    },
    body: {
      flex: 1,
      minWidth: 0,
    },
    title: {
      lineHeight: ROW_TITLE_LINE_HEIGHT,
    },
    arrow: {
      ref: arrow,
      marginLeft: 'auto',
      flexShrink: 0,
      opacity: 0,
      transform: 'translateX(-4px)',
      transition: 'opacity 120ms ease, transform 120ms ease',
    },
  }
})

interface ArticleRowProps {
  article: SanityArticleListItem
  color: string
  // Present only in flat search results, where each row shows its hub badge.
  hubTag?: HubTag
}

// A single article link row. Reused by both the per-hub tabs and the flat
// search results. Rendered as a real <a> (react-router Link) so the article
// slug shows in the browser status bar on hover.
export const ArticleRow = ({ article, color, hubTag }: ArticleRowProps) => {
  const { classes } = useStyles({ color })
  const TagIcon = hubTag?.icon
  const thumbnailUrl = buildSanityImageUrl(article.thumbnail, {
    width: THUMBNAIL_WIDTH * THUMBNAIL_FETCH_SCALE,
    height: THUMBNAIL_HEIGHT * THUMBNAIL_FETCH_SCALE,
    fit: 'crop',
    auto: 'format',
  })

  return (
    <Link to={`/app/resource-center/${article.slug}`} className={classes.article}>
      {thumbnailUrl ? (
        <Image
          src={thumbnailUrl}
          alt=''
          width={THUMBNAIL_WIDTH}
          height={THUMBNAIL_HEIGHT}
          radius='sm'
          fit='cover'
          className={classes.thumbnail}
        />
      ) : null}
      <div className={classes.body}>
        {hubTag && TagIcon ? (
          <Badge
            variant='light'
            color={color}
            size='sm'
            leftSection={<TagIcon size={BADGE_ICON_SIZE} />}
            mb={6}
          >
            {hubTag.title}
          </Badge>
        ) : null}
        <Text size='md' className={classes.title}>
          {article.title}
        </Text>
        {article.excerpt ? (
          <Text size='sm' color='dimmed' mt={2}>
            {article.excerpt}
          </Text>
        ) : null}
        {article.tags && article.tags.length > 0 ? (
          <Group spacing={4} mt={6}>
            {article.tags.map(tag => (
              <Badge key={tag._id} variant='outline' color='gray' size='xs'>
                {tag.title}
              </Badge>
            ))}
          </Group>
        ) : null}
      </div>
      <IconArrowRight size={ROW_ARROW_ICON_SIZE} className={classes.arrow} />
    </Link>
  )
}
